import { Injector } from '@angular/core';
import { EIremboFormlyFieldTypes } from '@irembo-andela/irembogov3-common';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { TConfigurationFormModelTypes } from '../../../../models/form-builder-types-configurations.model';
import {
  getDefaultBaseSettingsConfigFormly,
  generateDatasetSettingsConfig,
} from '../../form-fields-formly-settings/form-builder-default-settings-configuration-formly-fields.utils';
import {
  defaultTableWrapperConfigFormModel,
  tableWrapperFieldParseIntoSettingsFormModel,
  tableWrapperFieldUpdateFromSettingsFormModel,
} from '../table-wrapper-field-type.configuration';
import { BaseFormlyFieldConfiguration } from './base-formly-field-configuration';

export class CustomDatasetTableFormlyFieldConfiguration extends BaseFormlyFieldConfiguration {
  constructor(injector: Injector, field: FormlyFieldConfig) {
    super(injector, field);

    this.settingsFormlyModel = defaultTableWrapperConfigFormModel;
    this.settingsFormlyForm = [
      ...getDefaultBaseSettingsConfigFormly(this.formBuilderService),
      {
        key: 'tableSection',
        wrappers: ['panel-wrapper'],
        props: {
          label: 'Table Configuration',
        },
        fieldGroup: [
          {
            key: 'columns',
            type: EIremboFormlyFieldTypes.customexpansionrepeater,
            wrappers: ['custom-expansion-panel-with-icon'],
            props: {
              label: 'Columns',
              addText: 'Add Column',
              hideLabel: true,
              required: false,
              titleFieldName: 'label',
            },
            fieldArray: {
              fieldGroup: [
                {
                  key: 'label',
                  type: EIremboFormlyFieldTypes.input,
                  props: {
                    label: 'Column Label',
                    required: true,
                  },
                },
                {
                  key: 'key',
                  type: EIremboFormlyFieldTypes.input,
                  props: {
                    label: 'Column Key',
                    required: true,
                  },
                },
                {
                  key: 'sortable',
                  type: EIremboFormlyFieldTypes.checkbox,
                  props: {
                    label: 'Sortable',
                    formCheck: 'switch',
                  },
                },
                {
                  key: 'width',
                  type: EIremboFormlyFieldTypes.input,
                  props: {
                    label: 'Column Width',
                    placeholder: 'e.g., 100px, 10%, fit-content',
                  },
                },
                {
                  key: 'nowrap',
                  type: EIremboFormlyFieldTypes.checkbox,
                  props: {
                    label: 'No Text Wrap',
                    formCheck: 'switch',
                  },
                },
              ],
            },
          },
          {
            key: 'isSelectable',
            type: EIremboFormlyFieldTypes.checkbox,
            props: {
              label: 'Enable Row Selection',
              formCheck: 'switch',
            },
          },
          {
            key: 'maxSelectable',
            type: EIremboFormlyFieldTypes.input,
            props: {
              type: 'number',
              label: 'Maximum Selectable Rows',
              min: 1,
            },
            expressions: {
              hide: '!model.tableSection?.isSelectable',
            },
          },
          {
            key: 'selectionOutputPath',
            type: EIremboFormlyFieldTypes.input,
            props: {
              label: 'Selection Output Path',
              description:
                'Dot notation path to extract from selected row (e.g., user.id)',
            },
            expressions: {
              hide: '!model.tableSection?.isSelectable',
            },
          },
          {
            key: 'isSortVisible',
            type: EIremboFormlyFieldTypes.checkbox,
            props: {
              label: 'Show Sort Controls',
              formCheck: 'switch',
            },
          },
          {
            key: 'isSearchVisible',
            type: EIremboFormlyFieldTypes.checkbox,
            props: {
              label: 'Show Search',
              formCheck: 'switch',
            },
          },
          {
            key: 'searchPlaceholder',
            type: EIremboFormlyFieldTypes.input,
            props: {
              label: 'Search Placeholder',
            },
            expressions: {
              hide: '!model.tableSection?.isSearchVisible',
            },
          },
          {
            key: 'paginationVisible',
            type: EIremboFormlyFieldTypes.checkbox,
            props: {
              label: 'Show Pagination',
              formCheck: 'switch',
            },
          },
          {
            key: 'paginationInsideTable',
            type: EIremboFormlyFieldTypes.checkbox,
            props: {
              label: 'Pagination Inside Table',
              formCheck: 'switch',
            },
            expressions: {
              hide: '!model.tableSection?.paginationVisible',
            },
          },
          {
            key: 'pageSizes',
            type: EIremboFormlyFieldTypes.input,
            props: {
              label: 'Page Sizes',
              description: 'Enter numbers separated by commas (e.g., 10,25,50)',
              placeholder: '10,50,100',
            },
            parsers: [
              (value: string) =>
                value
                  .split(',')
                  .map(Number)
                  .filter(n => !isNaN(n)),
            ],
            expressions: {
              hide: '!model.tableSection?.paginationVisible',
            },
          },
          {
            key: 'tableClass',
            type: EIremboFormlyFieldTypes.input,
            props: {
              label: 'Table CSS Class',
              placeholder: 'table table-striped',
            },
          },
          {
            key: 'roundedTable',
            type: EIremboFormlyFieldTypes.checkbox,
            props: {
              label: 'Rounded Table',
              formCheck: 'switch',
            },
          },
        ],
      },
      generateDatasetSettingsConfig(),
      {
        key: 'datasetSection',
        wrappers: ['panel-wrapper'],
        props: {
          label: 'Search Configuration',
        },
        fieldGroup: [
          {
            key: 'searching',
            type: EIremboFormlyFieldTypes.checkbox,
            props: {
              label: 'Enable Search',
              formCheck: 'switch',
            },
          },
          {
            key: 'searchField',
            type: EIremboFormlyFieldTypes.input,
            props: {
              label: 'Search Field',
              description: 'Field to use for searching',
            },
            expressions: {
              hide: '!model.datasetSection?.searching',
            },
          },
        ],
      },
    ];
  }

  override parseFieldConfigIntoSettingsFormModel(): TConfigurationFormModelTypes {
    return tableWrapperFieldParseIntoSettingsFormModel(this.field);
  }

  override updateFieldConfigFromSettingsFormModel(model: any) {
    return tableWrapperFieldUpdateFromSettingsFormModel(model, this.field);
  }
}
